<script>
	import { createEventDispatcher } from 'svelte';
	import Icon from '../icons/Icon.svelte';

	const dispatch = createEventDispatcher();

	export let searchQuery = '';
	export let chatFilter = 'all'; // 'all', 'waiting', 'mine', 'bot'
	export let allCount = 0;
	export let waitingCount = 0;
	export let mineCount = 0;
	export let botCount = 0;
	export let filteredChats = [];
	export let activeChat = null;
	export let activeChatId = '';
	export let currentUserId = null;
	export let now = Date.now();

	export let getInitials = (name) => {
		if (!name) return "CL";
		const clean = name.replace(/[^\w\s]/gi, '').trim();
		const parts = clean.split(/\s+/).filter(Boolean);
		if (parts.length === 0) return "CL";
		if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
		return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
	};

	export let cleanSnippet = (text) => {
		if (!text) return "";
		return String(text).replace(/[*_~]/g, '').trim();
	};

	export let formatWaitingTime = (humanRequestedAt, currentNow = now) => {
		if (!humanRequestedAt) return "Aguardando";
		const requestedTime = new Date(humanRequestedAt).getTime();
		if (isNaN(requestedTime)) return "Aguardando";
		const diffMinutes = Math.floor(Math.max(0, currentNow - requestedTime) / 60000);
		if (diffMinutes < 1) return "Aguardando agora";
		if (diffMinutes === 1) return "Aguardando há 1 min";
		return `Aguardando há ${diffMinutes} min`;
	};

	function handleSelectChat(chat) {
		activeChatId = chat.id;
		dispatch('selectChat', chat.id);
	}

	function handleOpenSimulation() {
		dispatch('openSimulation');
	}
</script>

<aside class="wa-sidebar">
	<div class="sidebar-top-bar">
		<div class="search-input-wrap">
			<Icon name="search" size={14} color="var(--text-muted, #94A3B8)" />
			<input
				type="text"
				placeholder="Buscar conversa ou telefone..."
				bind:value={searchQuery}
			/>
			{#if searchQuery}
				<button class="clear-search-btn" on:click={() => searchQuery = ""} aria-label="Limpar busca">
					<Icon name="x" size={13} />
				</button>
			{/if}
		</div>

		<!-- Filtros de Conversas com Contadores (Fase 2C.2) -->
		<div class="filter-pills" role="tablist">
			<button
				class="filter-pill"
				class:active={chatFilter === 'all'}
				on:click={() => chatFilter = 'all'}
				role="tab"
				aria-selected={chatFilter === 'all'}
			>
				<span class="pill-text">Todas</span>
				<span class="pill-count">{allCount}</span>
			</button>
			<button
				class="filter-pill"
				class:active={chatFilter === 'waiting'}
				class:has-alert={waitingCount > 0}
				on:click={() => chatFilter = 'waiting'}
				role="tab"
				aria-selected={chatFilter === 'waiting'}
			>
				<span class="pill-text">Aguardando</span>
				<span class="pill-count">{waitingCount}</span>
			</button>
			<button
				class="filter-pill"
				class:active={chatFilter === 'mine'}
				on:click={() => chatFilter = 'mine'}
				role="tab"
				aria-selected={chatFilter === 'mine'}
			>
				<span class="pill-text">Meus</span>
				<span class="pill-count">{mineCount}</span>
			</button>
			<button
				class="filter-pill"
				class:active={chatFilter === 'bot'}
				on:click={() => chatFilter = 'bot'}
				role="tab"
				aria-selected={chatFilter === 'bot'}
			>
				<span class="pill-text">IA</span>
				<span class="pill-count">{botCount}</span>
			</button>
		</div>
	</div>

	<!-- Lista Rolável de Chats -->
	<div class="wa-chats-list">
		{#if filteredChats.length === 0}
			<div class="wa-empty-chats">
				<div class="empty-icon-wrap">
					<Icon name="message-square" size={28} />
				</div>
				<h4>Nenhuma conversa encontrada</h4>
				<p>{searchQuery ? 'Tente buscar com outro nome ou telefone.' : 'As novas mensagens recebidas no WhatsApp aparecerão aqui.'}</p>
			</div>
		{:else}
			{#each filteredChats as chat (chat.id)}
				<div
					class="wa-chat-item"
					class:active={(activeChat && activeChat.id === chat.id) || activeChatId === chat.id}
					class:needs-human={chat.needsHuman}
					on:click={() => handleSelectChat(chat)}
					role="button"
					tabindex="0"
				>
					<div class="wa-chat-avatar" class:initials-avatar={!chat.avatarUrl}>
						{#if chat.avatarUrl}
							<img src="{chat.avatarUrl}" alt="{chat.name}" class="avatar-photo-img" on:error={() => { chat.avatarUrl = null; }} />
						{:else}
							{getInitials(chat.name)}
						{/if}
						{#if chat.needsHuman}
							<span class="avatar-status-dot human" title="Aguardando atendente humano"></span>
						{:else if chat.botActive}
							<span class="avatar-status-dot bot" title="IA respondendo"></span>
						{/if}
					</div>

					<div class="wa-chat-info">
						<div class="wa-chat-top">
							<strong class="chat-name">{chat.name}</strong>
							<span class="chat-time">{chat.time || ''}</span>
						</div>
						<div class="chat-snippet-row">
							<span class="chat-snippet-text">{cleanSnippet(chat.lastSnippet)}</span>
							{#if chat.botActive === true && chat.needsHuman !== true}
								<span class="badge-status-pill bot-active" title="IA respondendo automaticamente">
									<Icon name="bot" size={11} />
									IA
								</span>
							{:else if chat.status === 'human_requested' || chat.needsHuman === true}
								<span class="badge-status-pill human-alert" title="Cliente aguardando atendimento humano">
									<Icon name="clock" size={11} />
									{formatWaitingTime(chat.humanRequestedAt, now)}
								</span>
							{:else if chat.status === 'human_active'}
								{#if chat.assignedOperatorId && currentUserId && chat.assignedOperatorId === currentUserId}
									<span class="badge-status-pill operator-mine" title="Atendimento assumido por você">
										<Icon name="user" size={11} />
										Você
									</span>
								{:else if chat.assignedOperatorId}
									<span class="badge-status-pill operator-other" title={`Atendido por ${chat.assignedOperatorName || 'Outro operador'}`}>
										<Icon name="users" size={11} />
										<span class="operator-name-text">{chat.assignedOperatorName || 'Operador'}</span>
									</span>
								{:else}
									<span class="badge-status-pill human-active" title="Atendimento humano ativo">
										<Icon name="user" size={11} />
										Humano
									</span>
								{/if}
							{:else}
								<span class="badge-status-pill bot-active" title="IA respondendo automaticamente">
									<Icon name="bot" size={11} />
									IA
								</span>
							{/if}
						</div>
					</div>
				</div>
			{/each}
		{/if}
	</div>

	<!-- Rodapé da Barra Lateral -->
	<div class="sidebar-footer">
		<button
			type="button"
			class="btn-open-sim"
			on:click={handleOpenSimulation}
		>
			<Icon name="flask" size={14} />
			<span>Laboratório de Testes da IA</span>
		</button>
	</div>
</aside>

<style>
/* BARRA LATERAL (CONVERSAS) */
	.wa-sidebar {
		background: var(--neutral-surface, #FFFFFF);
		border-right: 1px solid var(--neutral-border, #E2E8F0);
		display: flex;
		flex-direction: column;
		height: 100%;
		overflow: hidden;
	}

	.sidebar-top-bar {
		padding: 14px 16px;
		border-bottom: 1px solid var(--neutral-border, #E2E8F0);
		display: flex;
		flex-direction: column;
		gap: 10px;
	}

	.search-input-wrap {
		display: flex;
		align-items: center;
		gap: 8px;
		background: var(--neutral-surface-hover, #F1F5F9);
		border: 1px solid var(--neutral-border, #CBD5E1);
		border-radius: var(--radius-md, 10px);
		padding: 0 10px;
	}

	.search-input-wrap input {
		border: none;
		background: transparent;
		outline: none;
		width: 100%;
		font-size: 13px;
		padding: 8px 0;
		color: var(--text-primary, #0F172A);
	}

	.clear-search-btn {
		background: transparent;
		border: none;
		color: var(--text-muted, #94A3B8);
		cursor: pointer;
		display: flex;
		align-items: center;
		padding: 2px;
	}

	.filter-pills {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		gap: 3px;
		background: var(--neutral-surface-hover, #F1F5F9);
		padding: 3px;
		border-radius: 10px;
		width: 100%;
		box-sizing: border-box;
	}

	.filter-pill {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 4px;
		background: transparent;
		color: var(--text-secondary, #64748B);
		border: none;
		padding: 6px 2px;
		border-radius: 7px;
		font-size: 11px;
		font-weight: 700;
		cursor: pointer;
		transition: all 0.15s ease;
		white-space: nowrap;
		min-width: 0;
	}

	.filter-pill:hover:not(.active) {
		background: rgba(0, 0, 0, 0.04);
		color: var(--text-primary, #0F172A);
	}

	.filter-pill.active {
		background: #FFFFFF;
		color: var(--brand-primary, #FF6A00);
		box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
		font-weight: 800;
	}

	.filter-pill.has-alert {
		color: #DC2626;
	}

	.filter-pill.active.has-alert {
		background: #DC2626;
		color: #FFFFFF;
		box-shadow: 0 1px 3px rgba(220, 38, 38, 0.25);
	}

	.pill-text {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.pill-count {
		font-size: 10px;
		background: rgba(0, 0, 0, 0.06);
		padding: 1px 5px;
		border-radius: 6px;
		font-weight: 800;
		line-height: 1.2;
		flex-shrink: 0;
	}

	.filter-pill.active .pill-count {
		background: rgba(255, 106, 0, 0.12);
		color: var(--brand-primary, #FF6A00);
	}

	.filter-pill.active.has-alert .pill-count {
		background: rgba(255, 255, 255, 0.25);
		color: #FFFFFF;
	}

	.wa-chats-list {
		flex: 1;
		overflow-y: auto;
		display: flex;
		flex-direction: column;
	}

	.wa-empty-chats {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 48px 24px;
		text-align: center;
		gap: 8px;
		color: var(--text-secondary, #64748B);
	}

	.empty-icon-wrap {
		width: 52px;
		height: 52px;
		border-radius: 50%;
		background: var(--neutral-surface-hover, #F1F5F9);
		display: flex;
		align-items: center;
		justify-content: center;
		color: var(--text-muted, #94A3B8);
		margin-bottom: 4px;
	}

	.wa-empty-chats h4 {
		margin: 0;
		font-size: 15px;
		font-weight: 700;
		color: var(--text-primary, #0F172A);
	}

	.wa-empty-chats p {
		margin: 0;
		font-size: 12.5px;
		line-height: 1.4;
	}

	.wa-chat-item {
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 12px 16px;
		border-bottom: 1px solid var(--neutral-border, #F1F5F9);
		cursor: pointer;
		transition: all 0.15s ease;
		position: relative;
	}

	.wa-chat-item:hover {
		background: var(--neutral-surface-hover, #F8FAFC);
	}

	.wa-chat-item.active {
		background: #FFF7ED;
		border-left: 3px solid var(--brand-primary, #FF6A00);
	}

	.wa-chat-item.needs-human {
		background: #FFF5F5;
	}

	.wa-chat-avatar {
		width: 42px;
		height: 42px;
		min-width: 42px;
		max-width: 42px;
		min-height: 42px;
		max-height: 42px;
		border-radius: 50%;
		position: relative;
		flex-shrink: 0;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.wa-chat-avatar .avatar-photo-img {
		width: 42px;
		height: 42px;
		border-radius: 50%;
		object-fit: cover;
		flex-shrink: 0;
		display: block;
	}

	.initials-avatar {
		width: 42px;
		height: 42px;
		border-radius: 50%;
		background: #E2E8F0;
		color: #334155;
		font-size: 14px;
		font-weight: 800;
		display: flex;
		align-items: center;
		justify-content: center;
		position: relative;
		flex-shrink: 0;
	}

	.avatar-status-dot {
		width: 10px;
		height: 10px;
		border-radius: 50%;
		position: absolute;
		bottom: -2px;
		right: -2px;
		border: 2px solid #FFFFFF;
	}

	.avatar-status-dot.human {
		background: #DC2626;
		box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.2);
	}

	.avatar-status-dot.bot {
		background: #10B981;
		box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2);
	}

	.avatar-photo-img {
		width: 100%;
		height: 100%;
		border-radius: 50%;
		object-fit: cover;
		display: block;
		background: #E2E8F0;
		border: 1px solid rgba(0, 0, 0, 0.08);
	}

	

	

	.wa-chat-info {
		flex: 1;
		min-width: 0;
		display: flex;
		flex-direction: column;
		gap: 3px;
	}

	.wa-chat-top {
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	.chat-name {
		font-size: 13.5px;
		font-weight: 700;
		color: var(--text-primary, #0F172A);
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.chat-time {
		font-size: 11px;
		color: var(--text-muted, #94A3B8);
		flex-shrink: 0;
	}

	.chat-snippet-row {
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: 6px;
	}

	.chat-snippet-text {
		font-size: 12px;
		color: var(--text-secondary, #64748B);
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		flex: 1;
	}

	.badge-status-pill {
		display: inline-flex;
		align-items: center;
		gap: 3px;
		font-size: 10px;
		font-weight: 800;
		padding: 2px 6px;
		border-radius: 4px;
		text-transform: uppercase;
		flex-shrink: 0;
	}

	.badge-status-pill.human-alert {
		background: #FEE2E2;
		color: #DC2626;
		text-transform: none;
		font-weight: 700;
	}

	.badge-status-pill.human-active {
		background: #EFF6FF;
		color: #2563EB;
	}

	.badge-status-pill.operator-mine {
		background: #EFF6FF;
		color: #1D4ED8;
	}

	.badge-status-pill.operator-other {
		background: #F1F5F9;
		color: #475569;
		max-width: 120px;
	}

	.badge-status-pill .operator-name-text {
		max-width: 75px;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		display: inline-block;
		vertical-align: middle;
	}

	.badge-status-pill.bot-active {
		background: #DCFCE7;
		color: #16A34A;
	}

	.sidebar-footer {
		padding: 10px 14px;
		border-top: 1px solid var(--neutral-border, #E2E8F0);
		background: var(--neutral-surface-hover, #F8FAFC);
	}

	.btn-open-sim {
		width: 100%;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 6px;
		padding: 7px 12px;
		border-radius: 8px;
		border: 1px dashed var(--neutral-border, #CBD5E1);
		background: #FFFFFF;
		color: var(--text-secondary, #475569);
		font-size: 12px;
		font-weight: 700;
		cursor: pointer;
		transition: all 0.15s ease;
	}

	.btn-open-sim:hover {
		background: #FAF5FF;
		color: #7E22CE;
		border-color: #D8B4FE;
	}
</style>
